/**
 * SEO Sprint 4B (Feb 2026) — FAQ categories admin sub-section.
 *
 * Beheert de 11 FAQ-categorieën via:
 *   GET  /api/admin/faq-categories
 *   GET  /api/admin/faq-categories/{key}
 *   PUT  /api/admin/faq-categories/{key}
 *
 * Editor UX: linker rail met categorie-tabs, rechter form met
 * title/intro/items list-editor (zelfde pattern als de
 * DestinationsSubSection list-editor).
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { Save, Plus, Trash2, Loader2, HelpCircle } from "lucide-react";

const CATEGORY_OPTIONS = [
  { key: "shipping",          label: "1. Verzenden & Bestemmingen" },
  { key: "tariffs",           label: "2. Tarieven & Kosten" },
  { key: "payment",           label: "3. Betaling" },
  { key: "documents",         label: "4. Paklijst, Facturen & Douane" },
  { key: "tracking",          label: "5. Tracking & Status" },
  { key: "air_freight",       label: "6. Luchtvracht" },
  { key: "sea_freight",       label: "7. Zeevracht" },
  { key: "shop_drop_ship",    label: "8. Shop, Drop & Ship" },
  { key: "pickup_delivery",   label: "9. Pickup, Delivery & Opslag" },
  { key: "damage_insurance",  label: "10. Schade, Verzekering & Aansprakelijkheid" },
  { key: "local_branches",    label: "11. Lokale vestigingen" },
];

export default function FAQCategoriesSubSection() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("website:edit") || hasPermission("admin:all");

  const [activeKey, setActiveKey] = useState(CATEGORY_OPTIONS[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/faq-categories/${key}`);
      setData(r.data || null);
    } catch (e) {
      toast.error(formatApiError(e, "Kon FAQ-categorie niet laden"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(activeKey); }, [load, activeKey]);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setItemField = (idx, k, v) => setData((d) => {
    const items = [...(d.items || [])];
    items[idx] = { ...(items[idx] || {}), [k]: v };
    return { ...d, items };
  });
  const addItem = () => setData((d) => ({
    ...d, items: [...(d.items || []), { question: "", answer: "" }],
  }));
  const removeItem = (idx) => setData((d) => {
    const items = [...(d.items || [])];
    items.splice(idx, 1);
    return { ...d, items };
  });

  const save = async () => {
    if (!canEdit || !data) return;
    setSaving(true);
    try {
      const r = await adminApi.put(`/admin/faq-categories/${activeKey}`, {
        payload: {
          title: data.title,
          intro: data.intro,
          items: data.items,
        },
      });
      if (r.data) setData(r.data);
      toast.success("Categorie opgeslagen");
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        FAQ-categorie laden...
      </div>
    );
  }

  return (
    <div data-testid="faq-categories-sub-section">
      {!canEdit && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          U heeft alleen leesrechten — wijzigen vereist Owner/Admin of Content Manager.
        </div>
      )}

      <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 leading-relaxed">
        <strong>Belangrijke richtlijn:</strong> Gebruik veilige formuleringen
        ("aankomsttijden zijn indicatief", "douane verschilt per bestemming")
        en geef geen harde garanties of vaste transittijden. Zorg dat antwoorden
        kort maar volledig zijn — gemiddeld 80-150 woorden per antwoord.
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORY_OPTIONS.map((c) => {
          const active = activeKey === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveKey(c.key)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${active ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
              data-testid={`faq-category-${c.key}`}
            >
              <HelpCircle className="w-4 h-4" /> {c.label}
            </button>
          );
        })}
      </div>

      <div
        className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4"
        data-testid={`faq-category-editor-${activeKey}`}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-chivo font-bold text-lg text-[#082c50]">
            {data.title} <span className="text-gray-400 font-normal text-sm">({data.items?.length || 0} vragen)</span>
          </h3>
          <Button
            onClick={save}
            disabled={!canEdit || saving}
            className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white"
            data-testid={`faq-category-save-${activeKey}`}
          >
            {saving
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</>
              : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
          </Button>
        </div>

        <div>
          <Label>Categorie-titel</Label>
          <Input
            value={data.title || ""}
            onChange={(e) => setField("title", e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div>
          <Label>Intro (korte beschrijving boven de items)</Label>
          <Textarea
            rows={2}
            value={data.intro || ""}
            onChange={(e) => setField("intro", e.target.value)}
            disabled={!canEdit}
          />
        </div>

        <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-[#082c50]">
              FAQ items ({(data.items || []).length}/50)
            </span>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={addItem} className="rounded-lg text-xs">
                <Plus className="w-3 h-3 mr-1" /> Vraag toevoegen
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {(data.items || []).map((it, idx) => (
              <div key={idx} className="p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">Vraag #{idx + 1}</span>
                  {canEdit && (
                    <Button size="sm" variant="outline" onClick={() => removeItem(idx)} className="rounded-lg text-red-600 h-7 px-2">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide">Vraag</Label>
                    <Input
                      value={it.question || ""}
                      onChange={(e) => setItemField(idx, "question", e.target.value)}
                      disabled={!canEdit}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide">Antwoord</Label>
                    <Textarea
                      rows={4}
                      value={it.answer || ""}
                      onChange={(e) => setItemField(idx, "answer", e.target.value)}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>
            ))}
            {(data.items || []).length === 0 && (
              <div className="text-xs text-gray-500 italic py-2">Nog geen vragen — klik "Vraag toevoegen".</div>
            )}
          </div>
        </div>
      </div>

      <p className="mt-4 text-xs text-gray-500 leading-relaxed">
        Wijzigingen verschijnen direct op /faq. Alleen vragen + antwoorden die
        hier staan worden in het FAQPage JSON-LD schema gerenderd —
        Google's policy is "no hidden FAQ schema".
      </p>
    </div>
  );
}
